import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Youtube, Linkedin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_140bf421-cbc4-4de3-8362-159a067ec55a/artifacts/r2lyf9g6_golden-tree-events-logo.png";

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about-us' },
  { name: 'Our Events', path: '/events' },
  { name: 'Our Services', path: '/services' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact Us', path: '/contact-us' },
];

const legalLinks = [
  { name: 'Terms and Conditions', path: '/terms-and-conditions' },
  { name: 'Jury Terms', path: '/jury-terms-and-conditions' },
  { name: 'Winner Package Terms', path: '/winner-package-terms-and-conditions' },
  { name: 'Refund Agreement', path: '/refund-agreement' },
  { name: 'Privacy Policy', path: '/privacy-policy' },
  { name: 'Cookies Policy', path: '/cookies-policy' },
  { name: 'Refund Policy', path: '/refund-policy' },
];

const socialLinks = [
  { icon: Instagram, url: 'https://www.instagram.com/goldentreeeventsdubai/', label: 'Instagram' },
  { icon: Facebook, url: 'https://www.facebook.com/AwardsByGoldenTreeDubai', label: 'Facebook' },
  { icon: Youtube, url: 'https://www.youtube.com/channel/UCar_yBf569VvrfVy4V-qSXg/videos', label: 'YouTube' },
  { icon: Linkedin, url: 'https://linkedin.com/company/goldentreeeventsdubai', label: 'LinkedIn' },
];

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0a0a0a] border-t border-white/10">
      {/* Main Footer */}
      <div className="w-full px-4 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Golden Tree Events" className="h-12 w-auto" />
            </Link>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6">
              World's leading exhibition & award management company. Organizing award programs, conferences, and exhibitions in 20+ industries globally.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-montserrat text-white text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-montserrat text-white text-lg mb-6">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2">
            <h4 className="font-montserrat text-white text-lg mb-6">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-[#A1A1AA] text-sm">
                  9th Floor Office No 903-03<br />
                  MISA Business Center Dubai<br />
                  Iris Bay - Business Bay<br />
                  Dubai, UAE
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <div className="text-[#A1A1AA] text-sm">
                  <a href="tel:+971585862014" className="hover:text-[#D4AF37] transition-colors">+971 5858 6 2014</a>
                  <span className="mx-2">/</span>
                  <a href="tel:+971585872014" className="hover:text-[#D4AF37] transition-colors">+971 5858 7 2014</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <a href="mailto:support@goldentreeawards.com" className="text-[#A1A1AA] text-sm hover:text-[#D4AF37] transition-colors">
                  support@goldentreeawards.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div className="text-[#A1A1AA] text-sm">
                  <p className="font-medium text-white mb-1">Business Hours</p>
                  <p>Sunday to Friday</p>
                  <p>9:00 AM – 9:00 PM (Dubai Time)</p>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 pt-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${social.label.toLowerCase()}`}
                    className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="w-full px-4 sm:px-8 lg:px-12 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#52525B] text-sm">
              © {new Date().getFullYear()} Golden Tree Events LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-and-conditions" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
